import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, DollarSign, X, Loader2, ToggleLeft, ToggleRight, GripVertical } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import AdminLayout from '../../components/admin/AdminLayout';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPricing = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    package_id: '',
    name: '',
    amount: '',
    original_price: '',
    description: '',
    features: '',
    category: 'general',
    is_active: true,
    is_subscription: false,
    sort_order: 0,
    badge_text: ''
  });

  const categories = [
    { value: 'book', label: 'Book' },
    { value: 'coaching', label: 'Coaching' },
    { value: 'general', label: 'General' }
  ];

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/packages`);
      setPackages(response.data);
    } catch (error) {
      console.error('Error fetching packages:', error);
      // Try to seed defaults if none exist
      try {
        await axios.post(`${API}/admin/packages/seed-defaults`);
        const response = await axios.get(`${API}/admin/packages`);
        setPackages(response.data);
      } catch (seedError) {
        console.error('Error seeding defaults:', seedError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      package_id: pkg.package_id,
      name: pkg.name,
      amount: pkg.amount.toString(),
      original_price: pkg.original_price?.toString() || '',
      description: pkg.description,
      features: pkg.features?.join('\n') || '',
      category: pkg.category,
      is_active: pkg.is_active,
      is_subscription: pkg.is_subscription || false,
      sort_order: pkg.sort_order || 0,
      badge_text: pkg.badge_text || ''
    });
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingPackage(null);
    setFormData({
      package_id: '',
      name: '',
      amount: '',
      original_price: '',
      description: '',
      features: '',
      category: 'general',
      is_active: true,
      is_subscription: false,
      sort_order: packages.length + 1,
      badge_text: ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const packageData = {
      package_id: formData.package_id.toLowerCase().replace(/\s+/g, '_'),
      name: formData.name,
      amount: parseFloat(formData.amount),
      original_price: formData.original_price ? parseFloat(formData.original_price) : null,
      description: formData.description,
      features: formData.features.split('\n').filter(f => f.trim()),
      category: formData.category,
      is_active: formData.is_active,
      is_subscription: formData.is_subscription,
      sort_order: parseInt(formData.sort_order) || 0,
      badge_text: formData.badge_text || null
    };

    try {
      if (editingPackage) {
        await axios.put(`${API}/admin/packages/${editingPackage.package_id}`, packageData);
      } else {
        await axios.post(`${API}/admin/packages`, packageData);
      }
      setShowModal(false);
      fetchPackages();
    } catch (error) {
      console.error('Error saving package:', error);
      alert(error.response?.data?.detail || 'Failed to save package. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (pkg) => {
    try {
      await axios.put(`${API}/admin/packages/${pkg.package_id}`, {
        is_active: !pkg.is_active
      });
      fetchPackages();
    } catch (error) {
      console.error('Error toggling package:', error);
      alert('Failed to update package status.');
    }
  };

  const handleDelete = async (packageId) => {
    if (!window.confirm('Are you sure you want to delete this package? This cannot be undone.')) return;

    try {
      await axios.delete(`${API}/admin/packages/${packageId}`);
      fetchPackages();
    } catch (error) {
      console.error('Error deleting package:', error);
      alert('Failed to delete package.');
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'book': return 'bg-blue-100 text-blue-800';
      case 'coaching': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold">Pricing & Packages</h1>
            <p className="text-gray-600">Manage your product pricing ({packages.length} packages)</p>
          </div>
          <Button
            onClick={handleCreate}
            className="bg-gradient-to-r from-green-500 to-teal-500 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Package
          </Button>
        </div>

        {/* Packages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.map((pkg) => (
            <Card 
              key={pkg.package_id} 
              className={`border-2 relative ${pkg.is_active ? 'border-green-300' : 'border-gray-200 opacity-60'}`}
            >
              {pkg.badge_text && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-3 py-1 rounded-full text-xs font-bold">
                  {pkg.badge_text}
                </div>
              )}
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-2">
                    <Badge className={getCategoryColor(pkg.category)}>
                      {pkg.category}
                    </Badge>
                    {pkg.is_subscription && (
                      <Badge className="bg-purple-100 text-purple-800">Subscription</Badge>
                    )}
                  </div>
                  <button
                    onClick={() => handleToggleActive(pkg)}
                    className={`p-1 rounded ${pkg.is_active ? 'text-green-600' : 'text-gray-400'}`}
                    title={pkg.is_active ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
                  >
                    {pkg.is_active ? <ToggleRight className="h-6 w-6" /> : <ToggleLeft className="h-6 w-6" />}
                  </button>
                </div>

                <h3 className="font-bold text-xl mb-2">{pkg.name}</h3>
                
                <div className="flex items-baseline gap-1 mb-3">
                  <DollarSign className="h-5 w-5 text-green-600" />
                  <span className="text-3xl font-black text-green-600">{pkg.amount.toFixed(2)}</span>
                  {pkg.original_price && (
                    <span className="text-lg text-gray-400 line-through ml-2">${pkg.original_price.toFixed(2)}</span>
                  )}
                  {pkg.is_subscription && <span className="text-gray-500">/month</span>}
                </div>

                <p className="text-gray-600 text-sm mb-4 line-clamp-2">{pkg.description}</p>

                {pkg.features && pkg.features.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 mb-1">{pkg.features.length} features</p>
                    <ul className="text-xs text-gray-600 space-y-1">
                      {pkg.features.slice(0, 3).map((feature, i) => (
                        <li key={i} className="truncate">• {feature}</li>
                      ))}
                      {pkg.features.length > 3 && (
                        <li className="text-gray-400">...and {pkg.features.length - 3} more</li>
                      )}
                    </ul>
                  </div>
                )}

                <div className="flex justify-between items-center pt-4 border-t">
                  <span className="text-xs text-gray-400">ID: {pkg.package_id}</span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(pkg)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(pkg.package_id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {packages.length === 0 && (
          <div className="text-center py-12">
            <DollarSign className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No packages yet</h3>
            <p className="text-gray-500 mb-4">Create your first pricing package to get started</p>
            <Button onClick={handleCreate} className="bg-teal-600 text-white">
              <Plus className="h-4 w-4 mr-2" />
              Create Package
            </Button>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white">
                <h2 className="text-2xl font-bold">
                  {editingPackage ? 'Edit Package' : 'Create Package'}
                </h2>
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Package ID *</label>
                    <Input
                      value={formData.package_id}
                      onChange={(e) => setFormData({ ...formData, package_id: e.target.value })}
                      placeholder="e.g., coaching_premium"
                      required
                      disabled={!!editingPackage}
                      className={editingPackage ? 'bg-gray-100' : ''}
                    />
                    <p className="text-xs text-gray-500 mt-1">Unique identifier (no spaces)</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Category *</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full p-2 border rounded-md"
                      required
                    >
                      {categories.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Display Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Premium Coaching Package"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Sale Price ($) *</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="29.99"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Original Price ($)</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.original_price}
                      onChange={(e) => setFormData({ ...formData, original_price: e.target.value })}
                      placeholder="49.99 (shows crossed out)"
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave empty for no strikethrough</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Sort Order</label>
                    <Input
                      type="number"
                      value={formData.sort_order}
                      onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
                      placeholder="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Badge Text</label>
                    <Input
                      value={formData.badge_text}
                      onChange={(e) => setFormData({ ...formData, badge_text: e.target.value })}
                      placeholder="SALE"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Description *</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full p-2 border rounded-md"
                    rows={2}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Features (one per line)</label>
                  <textarea
                    value={formData.features}
                    onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                    className="w-full p-2 border rounded-md"
                    rows={4}
                    placeholder="150+ activities&#10;Step-by-step instructions&#10;Printable cards"
                  />
                </div>

                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-300"
                    />
                    <span className="text-sm font-medium">Active (visible to customers)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_subscription}
                      onChange={(e) => setFormData({ ...formData, is_subscription: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-300"
                    />
                    <span className="text-sm font-medium">Subscription (monthly billing)</span>
                  </label>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-green-600 text-white" disabled={saving}>
                    {saving ? 'Saving...' : (editingPackage ? 'Update Package' : 'Create Package')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminPricing;
